import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button, { ButtonType } from 'components/Button';
import AIThinkingLoader from 'components/FinancialCheckup/AICoach/AIThinkingLoader';
import { closeAIChat, ShiftImpactSource } from 'handlers/aiChat';
import { getAIChat } from 'selectors/aiChat';
import { getApplicationData } from 'selectors/getApplicationData';
import { selectAvailableShifts } from 'selectors/getAvailableShifts';
import { getShiftImpactState } from 'selectors/getShiftImpact';
import { getShiftImpact } from 'thunks';
import useBodyOverflow from 'hooks/useBodyOverflow';
import useRequestShift from 'hooks/useRequestShift';
import { useNavigate } from 'hooks/useNavigate';
import { RoutePath } from 'enums/Routes';

import styles from './ShiftImpact.module.scss';

const ShiftImpactWorkflow = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestShiftSignup, isSigningUp } = useRequestShift();
  const { activeWorkflow } = useSelector(getAIChat);
  const { application } = useSelector(getApplicationData);
  const { shifts } = useSelector(selectAvailableShifts);
  const { data, isLoading, isError } = useSelector(getShiftImpactState);
  const applicationId = application?.id;
  const shiftId = activeWorkflow?.shiftId;
  const shiftRequirementId = activeWorkflow?.shiftRequirementId;
  const shiftImpactSource = activeWorkflow?.shiftImpactSource ?? ShiftImpactSource.Dashboard;
  useBodyOverflow(true);

  useEffect(() => {
    if (!applicationId || !shiftId) return;

    dispatch(getShiftImpact({ applicationId, shiftId }));
  }, [applicationId, dispatch, shiftId]);

  const handleDeclineShift = () => {
    dispatch(closeAIChat());
  };

  const handleAcceptShiftFromDashboard = () => {
    const selectedShift = shiftId ? shifts.find((shift) => shift.shiftId === shiftId) : undefined;

    dispatch(closeAIChat());

    if (!selectedShift) return;

    navigate(RoutePath.ShiftDetails.replace(':id', selectedShift.shiftRequirementId.toString()) as RoutePath);
  };

  const handleAcceptShiftFromShiftDetails = () => {
    if (!shiftRequirementId) return;

    dispatch(closeAIChat());
    requestShiftSignup({ shiftRequirementId });
  };

  const handleAcceptShift = () => {
    if (shiftImpactSource === ShiftImpactSource.ShiftDetails) {
      handleAcceptShiftFromShiftDetails();
      return;
    }

    handleAcceptShiftFromDashboard();
  };

  if (isLoading) {
    return <AIThinkingLoader animating />;
  }

  if (isError || !data) {
    return <p className={styles.message}>Sorry, we couldn&apos;t load this shift analysis. Please try again.</p>;
  }

  const { explainer } = data;

  return (
    <div className={styles.container}>
      <p className={styles.greeting}>{explainer.greeting}</p>
      {explainer.context && <p className={styles.context}>{explainer.context}</p>}
      <p className={styles.conditionalText}>If you pick up this shift:</p>
      <ul className={styles.lines}>
        {explainer.lines.map((line, index) => (
          <li key={index} className={styles.line}>
            <span className={styles.lineIcon}>{line.icon}</span>
            <span className={styles.lineText}>{line.text}</span>
          </li>
        ))}
      </ul>
      <div className={styles.optionsContainer}>
        <Button
          type={ButtonType.Primary}
          className={styles.promptButton}
          onClick={handleAcceptShift}
          isLoading={isSigningUp}
        >
          Yes, let's pick up this shift!
        </Button>
        <Button type={ButtonType.Inverted} className={styles.promptButton} onClick={handleDeclineShift}>
          No, I can't make this shift
        </Button>
      </div>
    </div>
  );
};

export default ShiftImpactWorkflow;
